import { CID } from 'multiformats/cid';

import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { setUploadProgress } from '@/store/slices/poc/heliaSlice';
import { sendFileAnnouncement } from '@/store/thunks/poc/messaging/announceFileReadyThunk';

const RETRY_PROGRESS = 80;

/**
 * Retry a file announcement after checking that the CID's data is still
 * readable from the local blockstore. If the DAG is corrupt (e.g. the
 * `yield* is not iterable` error), the retry is aborted with a clear
 * error message instead of re-announcing a broken CID.
 */
export const retryFileAnnouncement = createAppAsyncThunk(
  'helia/retryFileAnnouncement',
  async(
    { uploadId }: { uploadId: string },
    { dispatch, extra: { pocServices }, getState }
  ) => {
    if (!Object.prototype.hasOwnProperty.call(getState().helia.uploadProgress, uploadId)) {
      return;
    }

    const uploadEntry = getState().helia.uploadProgress[uploadId];
    const {
      cid,
      fileName,
      sixId,
      metadata,
      retryCount = 0,
      progress,
    } = uploadEntry;

    if (cid == null || sixId == null || metadata == null) {
      dispatch(setUploadProgress({
        uploadId,
        fileName,
        stage: 'error',
        progress,
        error: 'Upload state is incomplete and cannot be retried.',
        retryCount,
      }));
      return;
    }

    const heliaNode = pocServices.p2pStackManagerService.getHeliaNode(sixId);

    if (heliaNode === null) {
      dispatch(setUploadProgress({
        uploadId,
        fileName,
        stage: 'error',
        progress: 0,
        error: 'P2P node not connected. Please refresh and try again.',
        retryCount,
      }));
      return;
    }

    // Check if the CID data can be read back from the local blockstore
    // Note: metadata.size is the original (pre-encryption) file size, not the
    // encrypted size stored under this CID, so we skip the size check.
    const parsedCid = CID.parse(cid);
    const verification = await pocServices.fileStoreService.verifyDagIntegrity(heliaNode, parsedCid);

    if (!verification.valid) {
      dispatch(setUploadProgress({
        uploadId,
        fileName,
        stage: 'error',
        progress: 0,
        error: 'File data is corrupt and cannot be retried. Please re-upload the file.',
        retryCount,
      }));
      return;
    }

    // CID is valid — proceed with re-announcement
    dispatch(setUploadProgress({
      uploadId,
      fileName,
      stage: 'announcing',
      progress: RETRY_PROGRESS,
      retryCount: retryCount + 1,
    }));

    void dispatch(sendFileAnnouncement({
      sixId,
      cid,
      uploadId,
      metadata,
      attemptCount: 1,
    }));
  }
);
